<script setup>
import { computed, provide, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import EventShow from 'dashboard/routes/dashboard/planning/events/Show.vue';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import DashboardAppFrame from 'dashboard/components/widgets/DashboardApp/Frame.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  eventId: {
    type: String,
    default: null,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();
const store = useStore();
const { uiSettings } = useUISettings();

const event = ref(null);
const isLoadingEvent = ref(false);
const isConversationLoading = ref(false);
const activeAppId = ref(null);

const currentChat = useMapGetter('getSelectedChat');
const conversationById = useMapGetter('getConversationById');
const contactConversationsUiFlags = useMapGetter(
  'contactConversations/getUIFlags'
);
const dashboardApps = useMapGetter('dashboardApps/getRecords');

const shouldShowConversationSidebar = computed(() => {
  if (!currentChat.value?.id) return false;
  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_scheduler_panel_open: isSchedulerPanelOpen,
  } = uiSettings.value;
  return Boolean(isContactSidebarOpen || isSchedulerPanelOpen);
});

const resolvedEventId = computed(
  () => props.eventId || route.params.eventId || null
);

const activeConversationId = computed(() => {
  if (props.conversationId) return String(props.conversationId);
  if (route.params.conversationId) return String(route.params.conversationId);
  return null;
});

const contactId = computed(() => event.value?.customer_id || null);

const linkedConversations = computed(() => {
  if (!contactId.value) return [];
  const all = store.getters['contactConversations/getContactConversation'](
    contactId.value
  );
  return [...(all || [])].sort(
    (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
  );
});

const conversationTabLabel = conversation => {
  const inboxName = store.getters['inboxes/getInbox'](
    conversation.inbox_id
  )?.name;
  const contactName = conversation.meta?.sender?.name;
  return inboxName || contactName || `#${conversation.id}`;
};

const detailTabs = computed(() => [
  {
    key: 'event',
    type: 'event',
    label: t('EVENT_INBOX.EVENT_TAB'),
    icon: 'i-lucide-calendar-days',
  },
  ...linkedConversations.value.map(conversation => ({
    key: `conversation-${conversation.id}`,
    type: 'conversation',
    conversationId: conversation.id,
    label: conversationTabLabel(conversation),
    icon: 'i-lucide-message-circle',
  })),
  ...dashboardApps.value.map(app => ({
    key: `app-${app.id}`,
    type: 'app',
    appId: app.id,
    label: app.title,
    icon: 'i-lucide-layout-dashboard',
  })),
]);

const activeTabKey = computed(() => {
  if (activeAppId.value) return `app-${activeAppId.value}`;
  if (activeConversationId.value) {
    return `conversation-${activeConversationId.value}`;
  }
  return 'event';
});

const isEventTabActive = computed(() => activeTabKey.value === 'event');
const isConversationTabActive = computed(() =>
  activeTabKey.value.startsWith('conversation-')
);
const isAppTabActive = computed(() => activeTabKey.value.startsWith('app-'));

const activeDashboardApp = computed(() => {
  if (!activeAppId.value) return null;
  return dashboardApps.value.find(app => app.id === activeAppId.value) || null;
});

const appChatContext = computed(() => {
  if (currentChat.value?.id) return currentChat.value;
  if (linkedConversations.value.length) return linkedConversations.value[0];
  const customer = event.value?.customer;
  return {
    id: null,
    meta: {
      sender: customer
        ? { id: customer.id, name: customer.name, email: customer.email }
        : { id: event.value?.customer_id },
    },
  };
});

const selectConversation = conversationDisplayId => {
  if (!resolvedEventId.value || !conversationDisplayId) return;
  activeAppId.value = null;
  router.push({
    name: 'event_inbox_conversation',
    params: {
      accountId: accountId.value,
      eventId: resolvedEventId.value,
      conversationId: conversationDisplayId,
    },
  });
};

const selectEventTab = () => {
  if (!resolvedEventId.value) return;
  activeAppId.value = null;
  router.push({
    name: 'event_inbox_event',
    params: {
      accountId: accountId.value,
      eventId: resolvedEventId.value,
    },
  });
};

const selectAppTab = appId => {
  activeAppId.value = appId;
  if (!resolvedEventId.value) return;
  if (route.name !== 'event_inbox_event') {
    router.push({
      name: 'event_inbox_event',
      params: {
        accountId: accountId.value,
        eventId: resolvedEventId.value,
      },
    });
  }
};

const onTabClick = tab => {
  if (tab.type === 'event') {
    selectEventTab();
    return;
  }
  if (tab.type === 'conversation') {
    selectConversation(tab.conversationId);
    return;
  }
  selectAppTab(tab.appId);
};

provide('eventInboxContext', {
  selectConversation,
  isActive: true,
});

const findConversation = id => conversationById.value(Number(id) || id);

const setActiveChat = async () => {
  const selectedConversation = findConversation(activeConversationId.value);
  if (!selectedConversation) return;

  try {
    await store.dispatch('setActiveChat', { data: selectedConversation });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
  } catch {
    // ignore
  }
};

const fetchConversationById = async () => {
  if (!activeConversationId.value) {
    if (!isAppTabActive.value) {
      store.dispatch('clearSelectedState');
    }
    return;
  }

  store.dispatch('clearSelectedState');
  const existingChat = findConversation(activeConversationId.value);
  if (existingChat) {
    await setActiveChat();
    return;
  }

  isConversationLoading.value = true;
  try {
    await store.dispatch('getConversation', activeConversationId.value);
    await setActiveChat();
  } catch {
    // ignore
  } finally {
    isConversationLoading.value = false;
  }
};

const fetchEvent = async () => {
  if (!resolvedEventId.value) return;
  isLoadingEvent.value = true;
  try {
    event.value = await store.dispatch(
      'planningEvents/show',
      resolvedEventId.value
    );
  } catch {
    event.value = null;
  } finally {
    isLoadingEvent.value = false;
  }
};

const fetchContactConversations = async contact => {
  if (!contact) return;
  const records = store.state.contactConversations?.records || {};
  const numericId = Number(contact);
  if (records[numericId] !== undefined || records[contact] !== undefined) {
    return;
  }
  await store.dispatch('contactConversations/get', contact);
};

watch(
  resolvedEventId,
  async (id, prev) => {
    if (!id || id === prev) return;
    activeAppId.value = null;
    await fetchEvent();
  },
  { immediate: true }
);

watch(contactId, id => {
  fetchContactConversations(id);
});

watch(
  activeConversationId,
  id => {
    if (id) activeAppId.value = null;
    fetchConversationById();
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('dashboardApps/get');
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-w-0 bg-white dark:bg-n-solid-1">
    <div
      class="flex items-center gap-4 px-4 border-b border-n-weak overflow-x-auto shrink-0 bg-white dark:bg-n-solid-1"
    >
      <button
        v-for="tab in detailTabs"
        :key="tab.key"
        type="button"
        class="inline-flex items-center gap-1.5 py-2.5 text-sm whitespace-nowrap border-b transition-colors max-w-[12rem] shrink-0"
        :class="
          activeTabKey === tab.key
            ? 'border-n-brand border-b-2 border-solid rounded-none text-n-blue-text font-medium'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        :title="tab.label"
        @click="onTabClick(tab)"
      >
        <Icon :icon="tab.icon" class="size-3.5 shrink-0" />
        <span class="truncate">{{ tab.label }}</span>
      </button>
      <Spinner
        v-if="contactConversationsUiFlags.isFetching"
        class="size-4 text-n-slate-10"
      />
    </div>

    <div class="flex-1 min-h-0 min-w-0 overflow-hidden relative">
      <div v-show="isEventTabActive" class="h-full overflow-y-auto">
        <div
          v-if="isLoadingEvent && !event"
          class="flex items-center justify-center h-full"
        >
          <Spinner />
        </div>
        <EventShow v-else-if="resolvedEventId" :event-id="resolvedEventId" />
      </div>

      <div v-if="isConversationTabActive" class="flex h-full min-w-0">
        <div
          v-if="isConversationLoading"
          class="flex items-center flex-1 justify-center"
        >
          <Spinner class="text-n-brand" />
        </div>
        <template v-else>
          <ConversationBox
            class="flex-1 [&.conversation-details-wrap]:!border-0"
            is-inbox-view
            hide-dashboard-apps
            :inbox-id="0"
            :is-on-expanded-layout="false"
          >
            <SidepanelSwitch v-if="currentChat.id" />
          </ConversationBox>
          <ConversationSidebar
            v-if="shouldShowConversationSidebar"
            :current-chat="currentChat"
          />
        </template>
      </div>

      <div v-if="isAppTabActive && activeDashboardApp" class="h-full min-w-0">
        <DashboardAppFrame
          :key="`${resolvedEventId}-${activeDashboardApp.id}`"
          is-visible
          :config="activeDashboardApp.content"
          :position="0"
          :current-chat="appChatContext"
        />
      </div>
    </div>
  </div>
</template>
